import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';
import { DataGrid, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import { InfraPlan } from '../../../models/plan/plan';
import PlannenActionMenu from './PlannenActionMenu';
import { authService } from '../../../services/auth.service';
import { contentService } from '../../../services/content.service';
import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { helperService } from '../../../services/helper.service';


interface TablePlannenProps {
  plans: InfraPlan[]; // ID of the current organisatie
  pageIndex: number; // Page index
  pageSize: number; 
  totalCount: number; // Total count of plans
  panelWidth: number;  // Receive panel width as prop
  mijnOrganisatie: MijnOrganisatieListItem; // ID of the current organisatie
  onPagination: (pageIndex: number, pageSize: number) => void; // callback function to trigger refresh
  onSorting: (sortBy: string, sortDirection: string) => void; // New prop
  onClickPlan: (planId: string) => void; // callback function to trigger refresh
}

const LS_VISIBLE_COLUMN_SETTING_KEY = "infplVisibleColumnSettings";

const getBreakpoint = (width: number) => {
  if (width < 201) return "xs";
  if (width < 401) return "sm";
  if (width < 601) return "md";
  if (width < 801) return "lg";
  if (width < 1001) return "xl";
  return "xxl";
};

const TablePlannen: React.FC<TablePlannenProps> = ({
  plans,
  pageIndex,
  pageSize,
  totalCount,
  panelWidth,
  mijnOrganisatie,
  onPagination,
  onSorting,
  onClickPlan
}) => {

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });

  const breakpoint = getBreakpoint(panelWidth);

  // const pageSizeOptions=[5, 10, 100, { value: 1000, label: '1,000' }, { value: -1, label: 'All' }];
  // const paginationModel = { page: 0, pageSize: 10 };
  // <div class="indication" [ngStyle]="{'background-color': row.colorRgb }"></div>

  const getBeoordelingTooltip = (key: string) => {
    let tooltipText = contentService.getBeoordelingStatusDescription(key);
    return tooltipText;
  };

  const getBeoordelingIcon = (key: string) => {
    let icon = contentService.getBeoordelingStatusIcon(key);
    return icon;
  };

  const gridColumns = useMemo(() => [
    {
      field: 'PlanId',
      headerName: 'Nummer',
      flex: 1,
      renderCell: (params: any) => <>{params.row.planId}</>,
    },
    {
      field: 'PlanNaam',
      headerName: 'Naam',
      flex: 3,
      renderCell: (params: any) => (
        <a onClick={() => onClickPlanLink(params.row.infplId)}>{params.row.planNaam}</a>
      ),
    },
    {
      field: 'Discipline',
      headerName: 'Discipline',
      flex: 1,
      renderCell: (params: any) => {
        const camelDiscipline = helperService.toCamelCase(params.row.discipline);
        return (
          <Tooltip title={params.row.discipline} placement="right" arrow>
            <span className={`category-icon category-icon-small ${camelDiscipline}-icon-white`}>
              {/* <span>{camelDiscipline}</span> */}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'OrganizationName',
      headerName: 'Organisatie',
      flex: 1,
      renderCell: (params: any) => <>{params.row.infplOrganizationName}</>,
    },
    {
      field: 'PlanType',
      headerName: 'Type',
      flex: 2,
      renderCell: (params: any) => <>{params.row.planType}</>,
    },
    {
      field: 'PlanStatus',
      headerName: 'Status',
      flex: 1,
      renderCell: (params: any) => <>{params.row.planStatus}</>,
    },
    {
      field: 'PlanStartDatum',
      headerName: 'Start',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.planStartDatum;
        if (!value) return '';
        const date = new Date(value);
        return isNaN(date.getTime())
          ? ''
          : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    },
    {
      field: 'PlanEindDatum',
      headerName: 'Eind',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.planEindDatum;
        if (!value) return '';
        const date = new Date(value);
        return isNaN(date.getTime())
          ? ''
          : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
      }
    },
    {
      field: 'Beoordeling',
      headerName: 'Beoordeling',
      flex: 1,
      renderCell: (params: any) => {

        // Geen waarde dan is dit een eigen plan
        if (!params.row.infplBeoordeling) {
          return (
            <></>
          );
        }
        
        const key = params.row.infplBeoordeling.id;
        let icon = getBeoordelingIcon(key);
        let tooltipText = getBeoordelingTooltip(key);

        return (
          <Tooltip title={tooltipText} placement="right" arrow>
            <span className={`beoordeling-icon ${key}`}>
              <span className="material-icons">{icon}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'Kosten',
      headerName: 'Kosten',
      flex: 2,
      renderCell: (params: any) => {
        const value = params.row.kosten;
        return value != null
          ? new Intl.NumberFormat('nl-NL', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2,
            }).format(value)
          : '-';
      }
    },
    ...(authService.canCrud() ? [{
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<InfraPlan>) => (
        <PlannenActionMenu mijnOrganisatie={mijnOrganisatie} params={params} refetchPlannen={refetchPlannen} />
      ),
    }] : []),
  ], []);

  // Load stored column visibility from localStorage
  useEffect(() => {
    const columns = getVisibleColumns();
    setVisibleColumns(columns);
  }, [panelWidth]);

  const getVisibleColumns = () => {
    const storedSettings = localStorage.getItem(LS_VISIBLE_COLUMN_SETTING_KEY);
    const parsedSettings = storedSettings ? JSON.parse(storedSettings) : {};

    if (parsedSettings[breakpoint]) {
      return parsedSettings[breakpoint];
    }
    return getDefaultColumns(breakpoint);
  };

  const getDefaultColumns = (bp: string) => {
    if (bp === "xs") return ['PlanNaam'];
    if (bp === "sm") return ['PlanId', 'PlanNaam', 'PlanStatus'];
    if (bp === "md") return ['PlanId', 'PlanNaam', 'PlanStatus', 'PlanType'];
    if (bp === "lg") return ['PlanId', 'PlanNaam', 'PlanStatus', 'PlanType', 'Discipline', 'Beoordeling'];
    if (bp === "xl") return ['PlanId', 'PlanNaam', 'PlanStatus', 'PlanType', 'Discipline', 'PlanStartDatum', 'PlanEindDatum', 'Beoordeling'];
    return gridColumns.map(col => col.field);
  };

  // Handle user toggling column visibility
  const handleColumnVisibilityChange = (newColumns: string[]) => {
    setVisibleColumns(newColumns);

    const storedSettings = localStorage.getItem(LS_VISIBLE_COLUMN_SETTING_KEY);
    const parsedSettings = storedSettings ? JSON.parse(storedSettings) : {};

    parsedSettings[breakpoint] = newColumns;
    localStorage.setItem(LS_VISIBLE_COLUMN_SETTING_KEY, JSON.stringify(parsedSettings));
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0]; // Get first sorting field
      onSorting(field, sort || "asc"); // Pass sorting info to Sidebar
    }
    else {
      onSorting("", ""); // Reset sorting
    }
  };

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    //console.log('onChangePagination', page, pageSize);
    setPaginationModel({ page, pageSize });
    onPagination(page, pageSize);
  };

  const onClickPlanLink = async (planId: string) => {
    onClickPlan(planId);
  };

  const refetchPlannen = async () => {
    // Use current pagination values
    onPagination(paginationModel.page, paginationModel.pageSize);
  }

  return (
    <>
      {/* {panelWidth} */}
      <div className="grid-responsive table-plannen">
        {/* <Paper sx={{ width: '100%' }}></Paper> */}
        <DataGrid
          rows={plans}
          columns={gridColumns}
          disableColumnFilter // Disable filtering
          disableRowSelectionOnClick
          getRowClassName={(params) => `discipline-row ${helperService.toCamelCase(params.row.discipline)}-border`}
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          sortingMode="server" // Disables built-in sorting
          onSortModelChange={handleSortChange} // Custom sort handling
          columnVisibilityModel={Object.fromEntries(gridColumns.map(col => [col.field, visibleColumns.includes(col.field)]))}
          onColumnVisibilityModelChange={(model) => handleColumnVisibilityChange(Object.keys(model).filter(key => model[key]))}
          sx={{ border: 0 }}
          slotProps={{
            footer: { className: 'footer-sticky' }, // Add custom class // 
          }}
          // checkboxSelection
        />
      </div>
    </>
  );
};

export default TablePlannen;